import { clientFetch } from '~/lib/.client/fetch';
import { type BoltConfigJson, BOLT_CONFIG_JSON } from '~/lib/persistence/frameworks';
import type { AppFile, AppFiles } from '~/lib/projects';
import { importPromise } from '~/lib/projects/importStore';
import { workbenchStore } from '~/lib/stores/workbench';
import { webcontainer as webcontainerPromise } from '~/lib/webcontainer';
import type { TemplateID } from '~/templates';
import type { TemplateResponse } from '~/types/template';
import { fromBase64 } from '~/utils/base64';
import { convertImportFilesToAppFiles } from '~/utils/convert-import-files';
import { createScopedLogger } from '~/utils/logger';
import { withResolvers } from '~/utils/promises';

const logger = createScopedLogger('TemplateStore');

export interface Template {
  id: TemplateID;
  files: AppFiles;
}

class TemplateStore {
  template?: Template;
  ready?: Promise<Template | undefined>;

  async tryFetchTemplate(message: string, abortSignal: AbortSignal, messageId: string) {
    const deferredPromise = withResolvers<Template | undefined>();

    try {
      this.ready = deferredPromise.promise;

      const request = await clientFetch('/api/template', {
        method: 'POST',
        signal: abortSignal,
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          messageId,
        }),
      });

      const { template } = await request.json<TemplateResponse>();

      if (template) {
        // patch title with a flag indicating it hasn't been changed
        if ('index.html' in template.files && typeof template.files['index.html'] === 'string') {
          template.files['index.html'] = template.files['index.html'].replace(/<title>/, '<title data-default>');
        }

        this.template = {
          id: template.id,
          files: convertImportFilesToAppFiles(template.files),
        };

        const start = Date.now();

        await this.#writeFiles(this.template);

        logger.debug(`Took ${Date.now() - start}ms to write all template files`);
      }

      logger.debug('Template fetched');
      deferredPromise.resolve(this.template);
    } catch (error) {
      logger.error('Failed to fetch template');

      deferredPromise.reject(error);
    } finally {
      /**
       * Mark the project as imported so that stores like the SupabaseEdgeFunctionsStore
       * knows that the project was imported.
       */
      importPromise.resolve();
    }

    return deferredPromise.promise;
  }

  async #writeFiles({ id, files }: Template) {
    const writeFile = async (entry: Pick<AppFile, 'isBinary' | 'fullPath' | 'contents'>) => {
      let content: string | Uint8Array;

      if (entry.isBinary) {
        content = fromBase64(entry.contents);
      } else {
        content = entry.contents;
      }

      await workbenchStore.writeFile(entry.fullPath, {
        type: 'file',
        content,
        isBinary: entry.isBinary,
        isStackBlitzBinary: false,
      });
    };

    for (const entry of Object.values(files)) {
      if (entry.type === 'file') {
        await writeFile(entry);
      }
    }

    const webcontainer = await webcontainerPromise;

    let boltConfig: BoltConfigJson = {};

    try {
      boltConfig = JSON.parse(await webcontainer.fs.readFile(BOLT_CONFIG_JSON, 'utf8'));
    } catch {
      // do nothing if it failed to parse or if the file does not exist
    }

    // write the template id to the bolt config
    boltConfig.template = id;

    await writeFile({
      fullPath: BOLT_CONFIG_JSON,
      contents: JSON.stringify(boltConfig, undefined, 2) + '\n',
      isBinary: false,
    });
  }
}

export const templateStore = new TemplateStore();
